import { useDocumentReferences } from '@ctw/shared/api/fhir/document';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { Button } from '@ctw/shared/components/button';
import { ContentTypeIcon } from '@ctw/shared/components/icons/content-type-icon';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { Tooltip } from '@ctw/shared/components/tooltip';
import {
  RelatedDocumentNarratives,
  useNarrativeForBinary,
} from '@ctw/shared/content/related-documents/related-document-narratives';
import { useResourceModal } from '@ctw/shared/context/resource-modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faChevronRight, faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';

export interface RelatedDocumentsListProps {
  binaryIds: Array<string>;
  // Documents identified only by DocumentReference id, not Binary id (e.g. an Encounter's ADT
  // messages). Requires `upid` to resolve.
  documentReferenceIds?: Array<string>;
  upid?: string;
  heading?: string;
}

export const RelatedDocumentsList = ({
  binaryIds,
  documentReferenceIds = [],
  upid,
  heading = 'Source Documents',
}: RelatedDocumentsListProps) => {
  const {
    data: resolvedAdtDocuments = [],
    isLoading: isLoadingAdtDocuments,
    isError: isAdtDocumentsError,
  } = useDocumentReferences(upid ?? '', documentReferenceIds);

  if (binaryIds.length === 0 && documentReferenceIds.length === 0) {
    return null;
  }

  // Drop an ADT document that resolves to a Binary already shown via `binaryIds`, so the same
  // underlying document isn't rendered twice.
  const additionalAdtDocuments = resolvedAdtDocuments.filter(
    (document) => document.binaryId === undefined || !binaryIds.includes(document.binaryId),
  );

  // Related binaries MUST render above ADT documents, regardless of the order the batched ADT
  // fetch resolves in. Concatenating in this order, and rendering from the single resulting
  // array, makes that grouping an explicit property of this list rather than an accident of
  // where each JSX block happens to sit. Neither group has an existing sort convention of its
  // own, so each keeps its incoming order.
  const orderedEntries: Array<RelatedDocumentListEntry> = [
    ...binaryIds.map((binaryId): RelatedDocumentListEntry => ({ kind: 'binary', binaryId })),
    ...additionalAdtDocuments.map(
      (document): RelatedDocumentListEntry => ({ kind: 'adt', document }),
    ),
  ];

  return (
    <div className={tw`space-y-2`}>
      <div className={tw`font-semibold text-lg`}>{heading}</div>
      <ul className={tw`m-0 list-none space-y-1 p-0`}>
        {orderedEntries.map((entry) =>
          entry.kind === 'binary' ? (
            <RelatedDocumentItem key={`binary-${entry.binaryId}`} binaryId={entry.binaryId} />
          ) : (
            <AdtDocumentItem key={`adt-${entry.document.id}`} document={entry.document} />
          ),
        )}
        {documentReferenceIds.length > 0 && isLoadingAdtDocuments && (
          <div className={tw`rounded-md border border-border-main px-3 py-4 shadow-sm`}>
            <LoadingSpinner message="Loading document..." centered={true} />
          </div>
        )}
        {documentReferenceIds.length > 0 && isAdtDocumentsError && (
          <div
            className={tw`flex items-center gap-2 rounded-md border border-border-main px-3 py-4 text-content-subtle shadow-sm`}
          >
            <FontAwesomeIcon icon={faCircleExclamation} className={tw`h-5 w-5`} />
            <div className={tw`font-medium text-[1rem] tracking-wide`}>
              Unable to load these documents
            </div>
          </div>
        )}
      </ul>
    </div>
  );
};

type RelatedDocumentListEntry =
  | { kind: 'binary'; binaryId: string }
  | { kind: 'adt'; document: DocumentModel };

const RELATED_DOCUMENT_DETAIL_DISPLAY_FIELDS = ['Date', 'Author'] as const;

interface ViewSourceDocumentButtonProps {
  resourceType: 'Binary' | 'DocumentReference';
  resourceId: string;
  contentType: string | undefined;
  enabled: boolean;
}

const ViewSourceDocumentButton = ({
  resourceType,
  resourceId,
  contentType,
  enabled,
}: ViewSourceDocumentButtonProps) => {
  const telemetry = useTelemetry();
  const { openResourceModal } = useResourceModal({
    layout: {
      mode: 'full',
      overflowBehavior: 'hidden',
      fullbleed: true,
      showCloseButton: false,
    },
  });

  return (
    <Tooltip
      id="related-document-tooltip"
      content="View source document"
      place="top"
      enabled={enabled}
    >
      <Button
        type="button"
        variant="unstyled"
        className={tw`flex shrink-0 items-center justify-center px-4 text-primary-text hover:opacity-80`}
        ariaLabel="View source document"
        disabled={!enabled}
        telemetryContext={{ resourceType, resourceId }}
        onClick={() => openResourceModal({ telemetry, resourceType, resourceId })}
      >
        <ContentTypeIcon contentType={contentType ?? ''} className={tw`h-5 w-5`} />
      </Button>
    </Tooltip>
  );
};

interface AdtDocumentItemProps {
  document: DocumentModel;
}

// A document identified only by DocumentReference id (e.g. an Encounter's ADT message). Already
// resolved by the parent list, so this renders without its own narrative expand/collapse.
const AdtDocumentItem = ({ document }: AdtDocumentItemProps) => (
  <div
    className={tw`flex w-full min-w-full items-center rounded-md border border-border-main shadow-sm`}
  >
    <div className={tw`flex w-full flex-1 items-center gap-3 px-3 py-4`}>
      <div className={tw`font-medium text-[1rem] text-content-main tracking-wide`}>
        {document.title}
      </div>
    </div>
    <ViewSourceDocumentButton
      resourceType="DocumentReference"
      resourceId={document.id}
      contentType={document.contentType}
      enabled={true}
    />
  </div>
);

export interface RelatedDocumentItemProps {
  binaryId: string;
}

export const RelatedDocumentItem = ({ binaryId }: RelatedDocumentItemProps) => {
  const { isLoading, isError, data } = useNarrativeForBinary({ binaryId });
  const canDisplayBinaryLink = !(isLoading || isError) && data !== undefined;
  const hasNarrative = (data?.cda?.content?.sections?.length ?? 0) > 0;
  const displayTitle = data?.title ?? data?.cda?.metadata?.document?.title ?? 'Untitled Document';
  const detailFields = data?.cda?.detailFields
    ?.filter((field) => RELATED_DOCUMENT_DETAIL_DISPLAY_FIELDS.includes(field.label as never))
    .map((field) => `${field.label}: ${field.value}`)
    .join(' | ');

  const isCollapsible = canDisplayBinaryLink && hasNarrative;
  const [isExpanded, setIsExpanded] = useState(true);
  const showsNarrative = isCollapsible && isExpanded;
  const narrativeRegionId = `related-document-narrative-${binaryId}`;

  return (
    <div className={tw`rounded-md border border-border-main shadow-sm`}>
      <div
        className={twx('flex w-full min-w-full items-center', {
          'rounded-b-none border-border-main border-b': showsNarrative,
        })}
      >
        <Button
          type="button"
          variant="link"
          className={tw`flex flex-1 items-center gap-1 hover:opacity-80`}
          ariaLabel={isExpanded ? 'Collapse document' : 'Expand document'}
          ariaExpanded={isExpanded}
          ariaControls={isCollapsible ? narrativeRegionId : undefined}
          disabled={!isCollapsible}
          onClick={() => {
            setIsExpanded((previous) => !previous);
          }}
        >
          <div className={tw`flex w-full items-center gap-3 px-3 py-4`}>
            {isCollapsible && (
              <FontAwesomeIcon
                icon={faChevronRight}
                className={twx('h-4 w-4 shrink-0 text-content-icon transition-transform', {
                  'rotate-90': isExpanded,
                })}
              />
            )}
            <div className={tw`block whitespace-nowrap text-left`}>
              {isLoading && <LoadingSpinner message="Loading document..." centered={true} />}
              {isError && (
                <div className={tw`flex items-center gap-2 text-content-subtle`}>
                  <FontAwesomeIcon icon={faCircleExclamation} className={tw`h-5 w-5`} />
                  <div className={tw`font-medium text-[1rem] tracking-wide`}>
                    Unable to load this document
                  </div>
                </div>
              )}
              {canDisplayBinaryLink && (
                <div className={tw`flex flex-col items-start justify-center`}>
                  <div className={tw`font-medium text-[1rem] text-content-main tracking-wide`}>
                    {displayTitle}
                  </div>
                  {!isEmpty(detailFields) && (
                    <div className={tw`text-content-subtle text-xs leading-tight`}>
                      {detailFields}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Button>
        <ViewSourceDocumentButton
          resourceType="Binary"
          resourceId={binaryId}
          contentType={data?.contentType}
          enabled={canDisplayBinaryLink}
        />
      </div>
      {showsNarrative && <RelatedDocumentNarratives binaryId={binaryId} />}
    </div>
  );
};
